import React from "react";
import ReactDOM from "react-dom";
import BreakpointStore from "../stores/BreakpointStore";
import { SubNavSectionModel } from "../models/SubNavSectionModel";
import { easeInOutCubic } from "../utils/dom";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { observer } from "mobx-react";
import throttle from "lodash/throttle";
import debounce from "lodash/debounce";
import unescape from "lodash/unescape";
import jump from "jump.js";
import classNames from "classnames";
import IntentBox from "./IntentBox/IntentBox";

enum Status {
  "Natural" = 0,
  "CrossOver" = 1,
  "Sticky" = 2,
}

enum ScrollDirection {
  "Down" = 0,
  "Up" = 1,
}

interface SubNavOpts {
  baseName: string;
  modelName?: string;
  sections: SubNavSectionModel[];
  defaultSectionTitle?: string;
  ignoreNav?: boolean;
  highlightPage?: string;
}

interface SubNavState {
  status: Status;
  isPrimaryPeeking: boolean;
  currentSection?: SubNavSectionModel;
  expanded?: boolean;
}

interface SubNavProps {
  opts: SubNavOpts;
}

@observer
class SubNav extends React.Component<SubNavProps, SubNavState> {
  isLoaded: Boolean = false;
  disableUpdateUrlOnScroll: Boolean = true;
  breakpoint: BreakpointStore = new BreakpointStore();
  navEl = null;
  triggerPoint: number = null;
  direction: ScrollDirection = ScrollDirection.Down;
  lastScrollPos: number = null;
  sections: SubNavSectionModel[] = [];

  get enabledSections(): SubNavSectionModel[] {
    return this.sections.filter((section) => !section.hidden);
  }

  get headerOffset(): number {
    return this.breakpoint.isMobile ? 53 : 81;
  }

  get ignoreNav(): boolean {
    return (
      document.location.href.toLowerCase().indexOf("ignorenav") !== -1 ||
      this.props.opts.ignoreNav
    );
  }

  constructor(props: SubNavProps) {
    super(props);

    this.state = {
      status: Status.Natural,
      isPrimaryPeeking: false,
      currentSection: null,
      expanded: false,
    };

    this.sections = props.opts.sections || [];
  }

  componentDidMount(): void {
    const navEl = document.getElementById("nav");
    if (!navEl) return;

    this.navEl = navEl;
    window.addEventListener(
      "scroll",
      debounce(() => this.tick(), 10)
    );
    window.addEventListener("load", () => {
      const section = this.getSectionNameFromUrl();

      this.setStickyPoint();

      if (section && !this.ignoreNav) {
        this.scrollToSection(section, false, () => {
          this.isLoaded = true;
          this.tick();
        });
      } else {
        this.disableUpdateUrlOnScroll = false;
        this.isLoaded = true;
        this.tick();
      }
    });
  }

  componentWillUpdate(nextProps: SubNavProps, nextState: SubNavState) {
    const { isPrimaryPeeking, status } = this.state;
    const body = document.getElementsByTagName("body")[0];
    const peekingClassName = `subNav-is-peeking`;
    const notNaturalClassName = `subNav-not-natural`;

    if (isPrimaryPeeking !== nextState.isPrimaryPeeking) {
      if (nextState.isPrimaryPeeking) {
        body.classList.add(peekingClassName);
      } else {
        body.classList.remove(peekingClassName);
      }
    }

    if (nextState.status === Status.Natural) {
      body.classList.remove(notNaturalClassName);
    } else {
      body.classList.add(notNaturalClassName);
    }
  }


  setStickyPoint(): void {
    if (!this.navEl) return null;
    if (this.state.status === Status.Sticky) return null;
    this.triggerPoint = this.navEl.offsetTop;
  }

  getSectionNameFromUrl(): string {
    // return window.location.href.split('/').pop().toLowerCase();
    let splitUrl = window.location.href.split("/");
    let section = null;
    const baseName = this.props.opts.baseName.toLowerCase().replace(/\//g, "");

    if (!splitUrl[splitUrl.length - 1].length) {
      splitUrl.pop();
    }

    section = splitUrl.pop().toLowerCase().split("?")[0];

    return section !== baseName ? section : null;
  }

  getSectionByName(sectionName): SubNavSectionModel {
    const sections: SubNavSectionModel[] = this.sections.filter(
      (section) => section.name === sectionName
    );
    return sections.length ? sections[0] : null;
  }

  updateUrl = throttle((sectionName: string) => {
    if (this.ignoreNav) return; //for dev
    const baseName = this.props.opts.baseName;
    const trimmedBaseName = baseName.endsWith("/")
      ? baseName.slice(0, -1)
      : baseName;

    if (this.getSectionNameFromUrl() !== sectionName) {
      history.pushState(
        null,
        null,
        sectionName ? `${baseName}${sectionName}` : trimmedBaseName
      );
    }
  }, 300);

  scrollToSection(
    sectionName: string,
    updateUrl: boolean = false,
    callback?: Function
  ) {
    const activeSection: SubNavSectionModel =
      this.getSectionByName(sectionName);
    this.disableUpdateUrlOnScroll = true;

    let jumpLocation = "body";
    var jump_element;
    if (activeSection && activeSection.name) {
      jumpLocation = `#${activeSection.name}`;
      jump_element = document.getElementById(activeSection.name);
    }

    if (jump_element) {
      jump(jumpLocation, {
        duration: 600,
        offset: this.headerOffset * -1,
        easing: easeInOutCubic,
        callback: () => {
          if (updateUrl) this.updateUrl(sectionName);
          this.disableUpdateUrlOnScroll = false;
          if (callback) callback.call({});
        },
      });
    }

    if (this.breakpoint.isMobile && this.state.expanded) {
      this.setState({
        expanded: false,
      });
    }
  }

  getCurrentActiveSection(): SubNavSectionModel {
    const isVisible = (el) => {
      if (!el) return false;
      const buffer: number = 20;
      var rect = el.getBoundingClientRect();
      var topPart = rect.top + this.headerOffset + buffer;
      return topPart < 0
        ? false
        : topPart <
            (window.innerHeight || document.documentElement.clientHeight);
    };

    const visibleSections: SubNavSectionModel[] = this.sections.filter(
      (section) => {
        if (!section.name) return false; //If no name is provided assume scroll top
        return isVisible(document.getElementById(section.name));
      }
    );

    return visibleSections.length ? visibleSections[0] : null;
  }

  tick(): void {
    if (!this.isLoaded) return;

    const scrollPos: number = window.pageYOffset;
    const direction: ScrollDirection =
      this.lastScrollPos <= scrollPos
        ? ScrollDirection.Down
        : ScrollDirection.Up;
    this.setStickyPoint();
    let newState: SubNavState = {
      status: Status.Natural,
      isPrimaryPeeking: false,
    };

    if (scrollPos > this.triggerPoint - this.headerOffset) {
      newState.status = Status.CrossOver;
    }

    if (scrollPos > this.triggerPoint) {
      newState.status = Status.Sticky;
    }

    if (
      direction === ScrollDirection.Up &&
      newState.status === Status.Sticky &&
      !this.disableUpdateUrlOnScroll
    ) {
      newState.isPrimaryPeeking = true;
    }

    newState.currentSection = this.getCurrentActiveSection();
    if (
      newState.currentSection !== this.state.currentSection &&
      !this.disableUpdateUrlOnScroll
    ) {
      this.updateUrl(
        newState.currentSection ? newState.currentSection.name : null
      );
    }

    if (newState.currentSection && newState.currentSection.name === "features") {
      const intentBoxSection = document.getElementById("intentBoxSection");
      if (intentBoxSection) {
        const contentSrc = intentBoxSection.getAttribute("data-content");
        ReactDOM.render(
          <IntentBox
            content={contentSrc ? window[contentSrc] : []}
            modelName={this.props.opts.modelName}
          />,
          intentBoxSection
        );
      }
    }

    this.setState(newState);
    this.lastScrollPos = scrollPos;
  }

  toggleSubNav(): void {
    const expanded: boolean = !this.state.expanded;
    this.setState({
      expanded,
    });
  }

  render() {
    const { status, isPrimaryPeeking, currentSection, expanded } = this.state;
    const { baseName, modelName, highlightPage } = this.props.opts;

    const renderSpecialLink = (section, index) => {
      const { name, title, highlight, absoluteUrl } = section;
      const itemStyle = classNames({
        "subNav__link--highlight": highlight,
        subNav__link: true,
        "subNav__link--active":
          highlightPage && unescape(highlightPage).includes(section.title)
            ? true
            : section === currentSection,
      });

      var url = absoluteUrl ? absoluteUrl : `${baseName}${name}`;

      return (
        <li key={`subNav__item${index}`} className="subNav__item">
          <a href={url} className={itemStyle}>
            {title}
          </a>
        </li>
      );
    };

    const renderNormalLink = (section, index) => {
      const { name, title, disableRedirect } = section;
      return (
        <li key={`subNav__item${index}`} className="subNav__item">
          {disableRedirect ? (
            <div
              onClick={(e) => {
                e.preventDefault();
                this.scrollToSection(name, true);
              }}
              className={`subNav__link ${
                section === currentSection ? "subNav__link--active" : ""
              }`}
            >
              {title}
            </div>
          ) : (
            <a
              onClick={(e) => {
                e.preventDefault();
                this.scrollToSection(name, true);
              }}
              href={`${baseName}${name}`}
              className={`subNav__link ${
                section === currentSection ? "subNav__link--active" : ""
              }`}
            >
              {title}
            </a>
          )}
        </li>
      );
    };

    return (
      <Router basename={this.props.opts.baseName}>
        <Route
          path="/"
          render={(props) => {
            const { history } = props;
            const { defaultSectionTitle } = this.props.opts;
            return (
              <div id="nav" className="subNav__wrap">
                <nav
                  className={`subNav subNav--${Status[status].toLowerCase()} ${
                    isPrimaryPeeking ? "subNav--peek" : ""
                  }`}
                >
                  <div className="subNav__container container container--large">
                    <div className="subNav__inner">
                      {modelName ? (
                        <div
                          className={`subNav__logo subNav__logo--${modelName
                            .toLowerCase()
                            .replace(/[^A-Za-z0-9_-]|(\bor|and\b)/gi, "")}`}
                          onClick={(e) => this.scrollToSection(null, true)}
                        />
                      ) : null}
                      <div
                        className={`subNav__current ${
                          expanded ? "subNav__current--expanded" : ""
                        }`}
                        onClick={() => this.toggleSubNav()}
                        dangerouslySetInnerHTML={{
                          __html:
                            (highlightPage && highlightPage) ||
                            (currentSection && currentSection.title) ||
                            (defaultSectionTitle && defaultSectionTitle),
                        }}
                      ></div>

                      <ul
                        className={`subNav__items ${
                          expanded ? "subNav__items--expanded" : ""
                        } ${modelName ? "subNav__items--isModel" : ""}`}
                      >
                        {this.enabledSections.map((section, index) => {
                          const { specialLink } = section;
                          return specialLink
                            ? renderSpecialLink(section, index)
                            : renderNormalLink(section, index);
                        })}
                      </ul>
                    </div>
                  </div>
                </nav>
              </div>
            );
          }}
        />
      </Router>
    );
  }
}

export default SubNav;
